// AP-033 · WebAudio-synthesized notification sounds. Zero external assets,
// zero copyright risk. Each sound is a tiny, unique "signature" tuned for
// professional/corporate feel (short, non-intrusive, distinct pitch profile).

let _ctx = null;
function ctx() {
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  return _ctx;
}

// Prime the audio context on first user gesture so later plays are audible.
if (typeof window !== "undefined" && !window.__jmsAudioPrimed) {
  const prime = async () => {
    try {
      const ac = ctx();
      if (ac && ac.state === "suspended") { await ac.resume(); }
    } catch {}
    window.__jmsAudioPrimed = true;
    // Do NOT auto-remove — keep listening so subsequent gestures can resume
    // a context that Chrome auto-suspended after inactivity.
  };
  window.addEventListener("click", prime);
  window.addEventListener("keydown", prime);
  window.addEventListener("touchstart", prime);
}

// Tone helper — a single oscillator with attack/release envelope.
function tone(ac, out, { freq = 880, type = "sine", start = 0, dur = 0.15, gain = 0.15, sweep = null } = {}) {
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  const t0 = ac.currentTime + start;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweep) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, sweep), t0 + dur);
  }
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + Math.min(0.02, dur / 3));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(out);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// Runs a "signature": ordered list of tones + master volume + optional noise.
// AP-033 Fase 2 fix · MUST be async to await resume() before scheduling so
// consecutive notifications all reproduce their sound (previously only the
// FIRST one played because Chrome auto-suspends the context after inactivity
// and the sync resume() call didn't unblock scheduling in time — oscillator
// start(t0) was scheduled at a frozen currentTime and silently dropped).
async function play(signature, volume = 0.6) {
  const ac = ctx();
  if (!ac) return;
  if (ac.state === "suspended") {
    try { await ac.resume(); } catch { return; }
  }
  // Some browsers keep the state as "interrupted" (Safari) — treat as suspended.
  if (ac.state !== "running") {
    try { await ac.resume(); } catch { return; }
  }
  const master = ac.createGain();
  master.gain.value = Math.max(0, Math.min(1, volume));
  master.connect(ac.destination);
  for (const t of signature) tone(ac, master, t);
}

// Each event kind gets a UNIQUE signature — pitch pattern + waveform combo.
const SIGNATURES = {
  // Customers — soft rising two-tone (welcoming).
  customer_new: [
    { freq: 660, type: "sine",     start: 0.00, dur: 0.10, gain: 0.22 },
    { freq: 990, type: "sine",     start: 0.09, dur: 0.14, gain: 0.20 },
  ],
  // Processes — new: single warm chime.
  process_new: [
    { freq: 523, type: "triangle", start: 0.00, dur: 0.12, gain: 0.22 },
    { freq: 659, type: "triangle", start: 0.10, dur: 0.16, gain: 0.20 },
  ],
  // Documents — quick paper "flip" tick.
  doc_attached: [
    { freq: 1200, type: "square",  start: 0.00, dur: 0.04, gain: 0.10 },
    { freq: 720,  type: "sine",    start: 0.05, dur: 0.10, gain: 0.15 },
  ],
  // Financeiro — pending: gentle two-tap reminder.
  pay_pending: [
    { freq: 440, type: "sine",     start: 0.00, dur: 0.10, gain: 0.18 },
    { freq: 440, type: "sine",     start: 0.16, dur: 0.10, gain: 0.18 },
  ],
  // Financeiro — confirmed: bright ascending arpeggio (positive).
  pay_confirmed: [
    { freq: 523, type: "triangle", start: 0.00, dur: 0.09, gain: 0.20 },
    { freq: 659, type: "triangle", start: 0.08, dur: 0.09, gain: 0.20 },
    { freq: 784, type: "triangle", start: 0.16, dur: 0.09, gain: 0.20 },
    { freq: 1046,type: "triangle", start: 0.24, dur: 0.14, gain: 0.22 },
  ],
  // Processo aprovado — success major third.
  process_approved: [
    { freq: 659, type: "sine",     start: 0.00, dur: 0.10, gain: 0.22 },
    { freq: 831, type: "sine",     start: 0.09, dur: 0.10, gain: 0.22 },
    { freq: 988, type: "sine",     start: 0.18, dur: 0.18, gain: 0.22 },
  ],
  // Processo rejeitado — descending alert.
  process_rejected: [
    { freq: 660, type: "sawtooth", start: 0.00, dur: 0.10, gain: 0.16 },
    { freq: 440, type: "sawtooth", start: 0.10, dur: 0.14, gain: 0.16 },
  ],
  // Processo publicado — premium sparkle (fast arpeggio + high sweep).
  process_published: [
    { freq: 784,  type: "triangle", start: 0.00, dur: 0.06, gain: 0.18 },
    { freq: 988,  type: "triangle", start: 0.06, dur: 0.06, gain: 0.20 },
    { freq: 1175, type: "triangle", start: 0.12, dur: 0.06, gain: 0.22 },
    { freq: 1568, type: "sine",     start: 0.18, dur: 0.24, gain: 0.20, sweep: 2093 },
  ],
  // Chat/DM — soft double-blip (chat-style).
  chat_dm: [
    { freq: 880, type: "sine",     start: 0.00, dur: 0.06, gain: 0.18 },
    { freq: 1174,type: "sine",     start: 0.08, dur: 0.08, gain: 0.20 },
  ],
  // Announcement — institutional two-tone.
  announcement: [
    { freq: 587, type: "square",   start: 0.00, dur: 0.10, gain: 0.14 },
    { freq: 587, type: "square",   start: 0.14, dur: 0.10, gain: 0.14 },
    { freq: 784, type: "sine",     start: 0.28, dur: 0.20, gain: 0.16 },
  ],
  // === AP-033 Fase 2 ===
  // Repasse (financeiro premium) — deep warm bell downstroke.
  settlement: [
    { freq: 392, type: "triangle", start: 0.00, dur: 0.14, gain: 0.20 },
    { freq: 523, type: "triangle", start: 0.10, dur: 0.14, gain: 0.20 },
    { freq: 784, type: "sine",     start: 0.22, dur: 0.20, gain: 0.20, sweep: 660 },
  ],
  // Agendamento novo — light "bloop" pair.
  appt_new: [
    { freq: 698, type: "sine",     start: 0.00, dur: 0.08, gain: 0.18 },
    { freq: 932, type: "sine",     start: 0.09, dur: 0.10, gain: 0.18 },
  ],
  // RH entrada — subtle single low tick.
  hr_in: [
    { freq: 349, type: "sine",     start: 0.00, dur: 0.06, gain: 0.14 },
    { freq: 523, type: "sine",     start: 0.07, dur: 0.10, gain: 0.16 },
  ],
  // RH saída — descending soft pair.
  hr_out: [
    { freq: 523, type: "sine",     start: 0.00, dur: 0.08, gain: 0.16 },
    { freq: 349, type: "sine",     start: 0.09, dur: 0.10, gain: 0.14 },
  ],
  // RH intervalo — very short muted tap.
  hr_break: [
    { freq: 440, type: "square",   start: 0.00, dur: 0.04, gain: 0.08 },
    { freq: 440, type: "square",   start: 0.08, dur: 0.04, gain: 0.08 },
  ],
  // Sistema — novo usuário — friendly "welcome".
  user_new: [
    { freq: 587, type: "sine",     start: 0.00, dur: 0.08, gain: 0.16 },
    { freq: 784, type: "sine",     start: 0.08, dur: 0.08, gain: 0.16 },
    { freq: 988, type: "sine",     start: 0.16, dur: 0.14, gain: 0.18 },
  ],
  // Sistema — permissão alterada — quick tri-blip (attention).
  perm_changed: [
    { freq: 880, type: "square",   start: 0.00, dur: 0.05, gain: 0.14 },
    { freq: 740, type: "square",   start: 0.06, dur: 0.05, gain: 0.14 },
    { freq: 880, type: "square",   start: 0.12, dur: 0.07, gain: 0.14 },
  ],
  // Sistema — update — soft ping.
  system_update: [
    { freq: 1046,type: "sine",     start: 0.00, dur: 0.08, gain: 0.14 },
    { freq: 1568,type: "sine",     start: 0.08, dur: 0.14, gain: 0.16, sweep: 2093 },
  ],
  // Fallback / generic.
  generic: [
    { freq: 880, type: "sine",     start: 0.00, dur: 0.10, gain: 0.15 },
  ],
  // Agenda reminders.
  reminder: [
    { freq: 988, type: "sine",     start: 0.00, dur: 0.10, gain: 0.18 },
    { freq: 784, type: "sine",     start: 0.14, dur: 0.10, gain: 0.18 },
  ],
};

export function playSound(soundKey, volume = 0.6) {
  const sig = SIGNATURES[soundKey] || SIGNATURES.generic;
  // Fire-and-forget async so multiple back-to-back calls each get their own
  // scheduling window after awaiting resume().
  play(sig, volume).catch(() => {});
}

export function isMuted(prefs, event) {
  if (!prefs?.sound_enabled) return true;
  if ((prefs.muted_events || []).includes(event?.type)) return true;
  if ((prefs.muted_categories || []).includes(event?.category)) return true;
  if (prefs.quiet_hours_enabled) {
    const now = new Date();
    const hm = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
    const { quiet_from = "22:00", quiet_to = "07:00" } = prefs;
    // Overnight window support (from > to means the window crosses midnight)
    if (quiet_from <= quiet_to) {
      if (hm >= quiet_from && hm <= quiet_to) return true;
    } else {
      if (hm >= quiet_from || hm <= quiet_to) return true;
    }
  }
  return false;
}

// Exported for the Preferences page so users can "try" a sound.
export const SOUND_KEYS = Object.keys(SIGNATURES);
